import { useNavigate } from 'react-router-dom';
import { Pin, Plane, MapPin, Users, Calendar, CheckCircle2, Clock, AlertCircle } from 'lucide-react';
import { cn } from '@/utils/cn';
import type { NoteListItem } from '@/types/notes.types';

interface NoteCardProps {
  note: NoteListItem;
}

export function NoteCard({ note }: NoteCardProps) {
  const navigate = useNavigate();

  // 摘要：取前 100 字符
  const excerpt = note.content
    ? note.content.replace(/[#*\-\[\]`]/g, '').slice(0, 100) + (note.content.length > 100 ? '...' : '')
    : '';

  return (
    <div
      onClick={() => navigate(`/notes/${note.id}`)}
      className={cn(
        'group relative bg-white border rounded-xl p-4 cursor-pointer transition-all hover:shadow-md',
        note.isPinned
          ? 'border-amber-200 bg-amber-50/30'
          : 'border-gray-200 hover:border-blue-300'
      )}
    >
      {/* 置顶标识 */}
      {note.isPinned && (
        <div className="absolute top-3 right-3">
          <Pin className="w-4 h-4 text-amber-500 fill-amber-500" />
        </div>
      )}

      {/* 标题 */}
      <h3 className="text-base font-semibold text-gray-900 mb-2 pr-6 line-clamp-1">
        {note.title}
      </h3>

      {/* 摘要 */}
      <p className="text-sm text-gray-600 line-clamp-2 mb-3">
        {excerpt || '暂无内容'}
      </p>

      {/* 关联信息 */}
      <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
        {note.routeCode && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-600 rounded">
            <Plane className="w-3 h-3" />
            {note.routeCode}
          </span>
        )}
        {note.airportCount > 0 && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-50 text-green-600 rounded">
            <MapPin className="w-3 h-3" />
            {note.airportCount} 个机场
          </span>
        )}
        {note.crewCount > 0 && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-50 text-purple-600 rounded">
            <Users className="w-3 h-3" />
            {note.crewCount} 人
          </span>
        )}
        {note.flightLogNum && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
            <Calendar className="w-3 h-3" />
            {note.flightLogNum}
          </span>
        )}
      </div>

      {/* 标签 */}
      {note.tags && note.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {note.tags.map((tag, i) => (
            <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded">
              #{tag}
            </span>
          ))}
        </div>
      )}

      {/* 时间和同步状态 */}
      <div className="mt-3 flex items-center justify-between text-xs text-gray-400">
        <span>更新于 {new Date(note.updatedAt).toLocaleDateString('zh-CN')}</span>
        {note.vaultSyncStatus && (
          <span className={cn(
            'inline-flex items-center gap-1',
            note.vaultSyncStatus === 'synced' && 'text-green-600',
            note.vaultSyncStatus === 'pending' && 'text-gray-400',
            note.vaultSyncStatus === 'failed' && 'text-red-500'
          )}>
            {note.vaultSyncStatus === 'synced' && <CheckCircle2 className="w-3 h-3" />}
            {note.vaultSyncStatus === 'pending' && <Clock className="w-3 h-3" />}
            {note.vaultSyncStatus === 'failed' && <AlertCircle className="w-3 h-3" />}
            {note.vaultSyncStatus === 'synced' && '已同步到 Obsidian'}
            {note.vaultSyncStatus === 'pending' && '同步中'}
            {note.vaultSyncStatus === 'failed' && '同步失败'}
          </span>
        )}
      </div>
    </div>
  );
}
